import { createRouter, createWebHistory } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';
import routes from '~pages';
import SignInVue from '@/pages/signin.vue';
import SettingsVue from '@/pages/settings.vue';
import MyAccountVue from '@/pages/profile/index.vue';
import PageNotFound from '@/pages/error/not-found.vue';
import PageUnauthorized from '@/pages/error/unauthorized.vue';
import { isEmpty, isObject } from 'lodash';
import type { StringMap } from '@/types/common';

// Whitelist pages that does not require authentication
const whitelistPages = [
	ROUTE.INDEX.NAME,
	ROUTE.SIGN_IN.NAME,
	ROUTE.SIGN_UP.NAME,
	ROUTE.USER.NAME,
	ROUTE.EVENT_VIEW.NAME,
	ROUTE.HELP.NAME,
	ROUTE.PRICING.NAME,
	ROUTE.PRICING_CALCULATOR.NAME,
	ROUTE.ERROR_404.NAME,
	ROUTE.ERROR_401.NAME,
	ROUTE.TERMS.NAME,
	ROUTE.PRIVACY_POLICY.NAME,
	ROUTE.CANCELLATION_POLICY.NAME,
	ROUTE.COOKIE_POLICY.NAME,
	ROUTE.COMMUNITY_GUIDELINES.NAME,
	ROUTE.ABOUT_US.NAME,
	ROUTE.SEARCH.NAME,
	ROUTE.GROUP.NAME,
	ROUTE.INVITATION.NAME,
	ROUTE.INVITATION_INVALID.NAME,
	ROUTE.INVITATION_VALID.NAME,
	ROUTE.UNSUBSCRIBE.NAME,
	ROUTE.VISIT_JAPAN.NAME,
	ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
	ROUTE.FEATURE_INVITATION.NAME,
	ROUTE.FEATURE_HOSTING_EVENTS.NAME,
	ROUTE.EVENT_ATTENDEES_FILE.NAME,
];

const protectedByRecaptchaPages = [ROUTE.EVENT_CREATE.NAME];

// Set page that has sticky action bar
const pagesWithActionBar = [ROUTE.EVENT_EDIT.NAME, ROUTE.EVENT_PREVIEW.NAME];

// Set page that need to show pictorial marks logo
const pictorialMarksLogoPages = [ROUTE.EVENT_VIEW.NAME, ROUTE.EVENT_PREVIEW.NAME];

const router = createRouter({
	history: createWebHistory(import.meta.env.VITE_SUBDOMAIN),
	routes: [
		{
			path: ROUTE.INDEX.PATH,
			redirect: { name: ROUTE.VISIT_JAPAN.NAME },
		},
		...routes,
		{
			name: ROUTE.SIGN_UP.NAME,
			path: ROUTE.SIGN_UP.PATH,
			component: SignInVue,
			props: { type: AUTH_FORM.SIGN_UP },
		},
		{
			name: ROUTE.PROFILE_ATTENDING.NAME,
			path: ROUTE.PROFILE_ATTENDING.PATH,
			component: MyAccountVue,
			props: { tab: MY_ACCOUNT_TABS.ATTENDING },
		},
		{
			name: ROUTE.PROFILE_HOSTING.NAME,
			path: ROUTE.PROFILE_HOSTING.PATH,
			component: MyAccountVue,
			props: { tab: MY_ACCOUNT_TABS.HOSTING },
		},
		{
			name: ROUTE.SETTINGS_PAYOUTS.NAME,
			path: ROUTE.SETTINGS_PAYOUTS.PATH,
			component: SettingsVue,
			props: { initialTab: SETTING_TABS.PAYOUTS },
		},
		{
			name: ROUTE.SETTINGS_SAVED_CARD.NAME,
			path: ROUTE.SETTINGS_SAVED_CARD.PATH,
			component: SettingsVue,
			props: { initialTab: SETTING_TABS.SAVED_CARDS },
		},
		{
			name: ROUTE.ERROR_404.NAME,
			path: ROUTE.ERROR_404.PATH,
			component: PageNotFound,
		},
		{
			name: ROUTE.ERROR_401.NAME,
			path: ROUTE.ERROR_401.PATH,
			component: PageUnauthorized,
		},
	],
	scrollBehavior(to, _from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		}
		if (to.hash) {
			return { el: to.hash, top: 80 }; // 80px above the element. `80` is height of header
		}
		return { top: 0 };
	},
});

// ============================================================================
// NAVIGATION GUARD HELPERS
// ============================================================================
// OPTIMIZATION PRINCIPLES:
// 1. Lightweight operations run first (analytics, layout)
// 2. Cheap synchronous checks before expensive async calls
// 3. Skip unnecessary logic - only fetch data for relevant routes
// 4. Share fetched data - one API call for multiple checks
// 5. Fail fast - check non-auth routes before auth requirements
// ============================================================================

/**
 * Helper function for legacy redirect
 */
function handleLegacyRedirect(to: RouteLocationNormalized) {
	switch (to.name) {
		case ROUTE.SIGN_IN.NAME:
		case ROUTE.EVENT_CREATE.NAME:
			openLink(`${import.meta.env.VITE_LEGACY_PEATIX_URL}${ROUTE.SIGN_IN.PATH}`, '_self');
			break;
		case ROUTE.SIGN_UP.NAME:
			openLink(`${import.meta.env.VITE_LEGACY_PEATIX_URL}${ROUTE.SIGN_UP.PATH}`, '_self');
			break;
		default:
			openLink(import.meta.env.VITE_LEGACY_PEATIX_URL, '_self');
			break;
	}
}

/**
 * Captures UTM parameters from URL query string and persists them in session storage.
 * This ensures UTM values are preserved across SPA navigation and available for analytics.
 * Industry standard: Capture on every route change to support deep links and marketing campaigns.
 */
function captureUtmParams(to: RouteLocationNormalized) {
	const storedUtmValues = getSessionStorage(SESSION_STORAGE_ITEMS.UTM);
	const utmValues: StringMap = isObject(storedUtmValues) ? (storedUtmValues as StringMap) : {};

	// Capture all standard UTM parameters from the route query
	const newUtmValues: StringMap = {
		utm_source: (to.query.utm_source as string) || null,
		utm_medium: (to.query.utm_medium as string) || null,
		utm_campaign: (to.query.utm_campaign as string) || null,
		utm_term: (to.query.utm_term as string) || null,
		utm_content: (to.query.utm_content as string) || null,
	};

	// Check if there are any valid UTM values in the current URL
	const hasValidUtmValues = Object.values(newUtmValues).some((value) => value !== undefined && value !== null && value !== '');

	// Stop if no UTM parameters in the current URL
	if (!hasValidUtmValues) {
		return;
	}

	// Update stored UTM values with new ones (preserve previous values if new ones are empty)
	// This ensures first-touch attribution is maintained throughout the session
	let isUtmUpdated = false;
	Object.keys(newUtmValues).forEach((key) => {
		const value = newUtmValues[key];
		if (value && value !== '') {
			utmValues[key] = value;
			isUtmUpdated = true;
		}
	});

	// Clean up any empty values
	Object.keys(utmValues).forEach((key) => {
		if (!utmValues[key] || utmValues[key] === '') {
			delete utmValues[key];
		}
	});

	if (isUtmUpdated) {
		setSessionStorage(SESSION_STORAGE_ITEMS.UTM, JSON.stringify(utmValues));
	}
}

/**
 * Check if route requires event data
 * Only fetch event details for routes that actually need it
 */
function isEventRoute(routeName: string | symbol | null | undefined): boolean {
	if (!routeName || typeof routeName === 'symbol') return false;
	return [
		ROUTE.EVENT_VIEW.NAME,
		ROUTE.EVENT_EDIT.NAME,
		ROUTE.EVENT_PREVIEW.NAME,
	].includes(routeName);
}

/**
 * Event-specific guards (runs only for event routes)
 * Fetches event data ONCE and applies multiple checks
 * @returns redirect config or null to continue
 */
async function handleEventSpecificGuards(
	to: RouteLocationNormalized,
	eventStore: ReturnType<typeof useEventStore>,
): Promise<any> {
	// Skip if not an event route
	if (!isEventRoute(to.name) || !to.params.id) {
		return null;
	}

	const eventId = Number(to.params.id);

	try {
		// Fetch event data ONCE for all event-related checks
		await eventStore.retrieveEventDetail(eventId);

		// CHECK 1: Child session redirect (for EVENT_VIEW only)
		if (to.name === ROUTE.EVENT_VIEW.NAME) {
			if (eventStore.isSessionEvent && eventStore.edit.data?.recurringEventId) {
				return {
					name: ROUTE.EVENT_VIEW.NAME,
					params: { id: eventStore.edit.data.recurringEventId },
					replace: true,
				};
			}
		}

		// CHECK 2: Redirect recurring event editing to preview mode (for EVENT_EDIT only)
		// This allows organizers to access management tabs while keeping event details read-only
		if (to.name === ROUTE.EVENT_EDIT.NAME) {
			if (eventStore.isRecurringEvent) {
				return {
					name: ROUTE.EVENT_PREVIEW.NAME,
					params: { id: eventId },
					replace: true,
				};
			}
		}
	} catch (error) {
		// If fetch fails, continue normally (page will handle the error)
	}

	return null;
}

/**
 * Country and Access Control Guard
 * Validates user's country and manages access to region-specific features.
 * Redirects non-US users to legacy site.
 * Only runs for authenticated users (PRIORITY 4 in execution).
 * @returns true if navigation was redirected, false to continue
 */
async function handleCountryAccessControl(
	to: RouteLocationNormalized,
	authStore: ReturnType<typeof useAuthStore>,
	profileStore: ReturnType<typeof useProfileStore>,
	layoutStore: ReturnType<typeof useLayoutStore>,
): Promise<boolean> {
	const isLoggedIn = authStore.isLoggedIn;
	const isJapanDiscoveryPage =
		to.name === ROUTE.VISIT_JAPAN.NAME || to.name === ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME;

	// Fetch profile country if logged in and not cached
	let profileCountry = profileStore.profile?.countryId;
	if (isLoggedIn && !profileCountry) {
		try {
			await profileStore.myAccountInfo();
			profileCountry = profileStore.profile?.countryId;
		} catch {
			// Profile fetch failed - treat as non-US user
			authStore.removeUser();
			handleLegacyRedirect(to);
			return true;
		}
	}

	// Determine access based on page type and user country
	if (isJapanDiscoveryPage) {
		layoutStore.isAccessible = true;
	} else if (isLoggedIn) {
		if (profileCountry && profileCountry !== DEFAULT_COUNTRY) {
			// Non-US user trying to access restricted content
			authStore.removeUser();
			handleLegacyRedirect(to);
			return true;
		}
		layoutStore.isAccessible = true;
	} else {
		// Logged out users allowed (IP check TODO)
		layoutStore.isAccessible = true;
	}

	return false;
}

/**
 * Authentication Guard
 * Handles authentication requirements and sign-in/sign-up page access.
 * Cheap check - returns immediately for public routes (PRIORITY 3 in execution).
 * @returns navigation object or null to continue
 */
function handleAuthenticationGuard(
	to: RouteLocationNormalized,
	authStore: ReturnType<typeof useAuthStore>,
): any {
	const isWhiteListPath = whitelistPages.includes(to.name as string);

	// Redirect unauthenticated users to sign-in for protected routes
	if (!authStore.isLoggedIn && !isWhiteListPath) {
		if (to.path === ROUTE.SIGN_OUT.PATH && import.meta.env.VITE_ENV !== ENVIRONMENT.DEVELOPMENT) {
			const redirectSignoutUrl = `${import.meta.env.VITE_LEGACY_PEATIX_URL}/__cc.cgi`;
			openLinkInCurrentTab(redirectSignoutUrl);
			return null;
		}

		return {
			name: ROUTE.SIGN_IN.NAME,
			...(to.name === ROUTE.EVENT_CREATE.NAME && {
				query: {
					redirect: ROUTE.EVENT_CREATE.NAME,
				},
			}),
		};
	}

	// Prevent authenticated users from accessing sign-in/sign-up pages
	if (authStore.isLoggedIn && (to.name === ROUTE.SIGN_IN.NAME || to.name === ROUTE.SIGN_UP.NAME)) {
		return {
			path: '',
			replace: true,
		};
	}

	return null;
}

/**
 * Analytics and Tracking
 * Captures UTM parameters and tracks page views.
 * Lightweight, non-blocking - runs first for all routes (PRIORITY 1 in execution).
 */
function handleAnalyticsTracking(
	to: RouteLocationNormalized,
	profileStore: ReturnType<typeof useProfileStore>,
): void {
	// Capture UTM parameters for marketing attribution
	captureUtmParams(to);

	// Track landing page visits
	if (to.path === ROUTE.INDEX.PATH) {
		const { trackEvent } = useGtm();
		trackEvent({
			user_id: profileStore.profileId,
			event: GTM_EVENT_NAME.VIEW_LANDING_PAGE_SUCCESS,
		});
	}
}

/**
 * Layout and UI Configuration
 * Sets up page-specific layout flags and UI elements.
 * Lightweight synchronous operations - runs early (PRIORITY 2 in execution).
 */
function configureLayoutSettings(
	to: RouteLocationNormalized,
	layoutStore: ReturnType<typeof useLayoutStore>,
): void {
	// Reset layout flags to defaults
	layoutStore.isPublic = false;
	layoutStore.isFullScreen = false;

	// Configure pictorial marks logo display
	if (pictorialMarksLogoPages.includes(to.name as string)) {
		layoutStore.isPublic = true;
	}

	// Configure reCAPTCHA badge visibility
	if (protectedByRecaptchaPages.includes(to.name as string)) {
		window.document.body.classList.add('protect-by-recaptcha');
	} else {
		window.document.body.classList.remove('protect-by-recaptcha');
	}

	// Configure action bar display
	if (to.name && pagesWithActionBar.includes(to.name.toString())) {
		layoutStore.hasActionBar = true;
	} else {
		layoutStore.hasActionBar = false;
	}
}

// ============================================================================
// MAIN NAVIGATION GUARD
// ============================================================================
// Optimized execution order:
// 1. Lightweight non-blocking operations (analytics, layout)
// 2. Authentication checks (cheap, no API calls for public routes)
// 3. Country access control (only for authenticated users)
// 4. Event-specific guards (only for event routes, one API call)
// ============================================================================

router.beforeEach(async (to, _from, next) => {
	// Initialize stores
	const authStore = useAuthStore();
	const profileStore = useProfileStore();
	const layoutStore = useLayoutStore();
	const eventStore = useEventStore();

	// PRIORITY 1: Analytics and tracking (lightweight, non-blocking)
	// Runs for ALL routes, no conditional logic needed
	handleAnalyticsTracking(to, profileStore);

	// PRIORITY 2: Layout configuration (lightweight, non-blocking)
	// Runs for ALL routes, pure synchronous operations
	configureLayoutSettings(to, layoutStore);

	// PRIORITY 3: Authentication check (cheap, no API for public routes)
	// Most routes are public, this returns null immediately
	const authRedirect = handleAuthenticationGuard(to, authStore);
	if (authRedirect) {
		next(authRedirect);
		return;
	}

	// PRIORITY 4: Country access control (only for authenticated users)
	// Skipped entirely for logged-out users or Japan Discovery pages
	const wasBlocked = await handleCountryAccessControl(to, authStore, profileStore, layoutStore);
	if (wasBlocked) {
		return;
	}

	// PRIORITY 5: Event-specific guards (only for event routes)
	// Skips entirely for non-event routes (most routes)
	// Fetches data ONCE and applies all event-related checks
	const eventRedirect = await handleEventSpecificGuards(to, eventStore);
	if (eventRedirect) {
		next(eventRedirect);
		return;
	}

	// All guards passed - proceed with navigation
	next();
});

router.afterEach((to) => {
	// Ignore this route when has query
	if (to.name === ROUTE.SETTINGS.NAME && !isEmpty(to.query)) {
		return;
	}

	// Reset translation state when navigating away from pages that use translation
	const translationPages = [ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME, ROUTE.EVENT_VIEW.NAME];

	// If navigating to a page that doesnt use translation, reset the translation state
	if (!translationPages.includes(to.name as string)) {
		const { setTranslatedView } = useTranslation();
		setTranslatedView(false);
	}

	const { setDocumentTitle } = useSetDocumentTitle();
	setDocumentTitle(to.name);
});

export default router;
